import { getStoredToken } from './authService';
import { API_BASE_URL } from '../config/api';
import { LessonSummary, GenericLesson, LessonService } from './lessonServiceTypes';
import { generateImageForLesson, generateImageForCategory } from './imageService';

// ========================================
// Types for Knowledge Category Service
// ========================================

export interface LessonStructure {
  has_vocabulary: boolean;
  custom_fields: string[];
}

export interface KnowledgeCategory {
  category_id: string;
  user_id: string;
  category_name: string;
  category_slug: string;
  description?: string;
  prompt_template: string;
  lesson_structure: LessonStructure;
  image_url?: string;
  image_prompt?: string;
  created_at: string;
  updated_at: string;
  owner_display_name?: string;
  owner_username?: string;
  is_favorite?: boolean;
}

export interface KnowledgeCategoryLesson extends GenericLesson {
  lesson_id?: string;
  lesson_name: string;
  lesson_objective: string;
  category: string;
  lesson_content: {
    markdown: string;
    [key: string]: any;
  };
  image_url?: string;
  image_prompt?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateCategoryData {
  category_name: string;
  description?: string;
  prompt_template: string;
  lesson_structure: LessonStructure;
}

export interface UpdateCategoryData {
  category_name?: string;
  description?: string;
  prompt_template?: string;
  lesson_structure?: LessonStructure;
}

// Helper function to get auth token
const getAuthToken = async (): Promise<string> => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// ========================================
// Knowledge Category Service Class
// ========================================

export class KnowledgeCategoryService {
  /**
   * Get all knowledge categories for user
   */
  static async getCategories(): Promise<{
    categories: KnowledgeCategory[];
    total: number;
  }> {
    try {
      const result = await apiCall('/knowledge-categories');

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch categories');
      }

      // Extract from result.data since sendSuccess wraps data
      return {
        categories: result.data.categories || [],
        total: result.data.total || 0
      };
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Get all knowledge categories with their lesson counts in one query
   * This is more efficient than calling getCategories() and then getPastLessons() for each category
   */
  static async getCategoriesWithCounts(): Promise<{
    categories: (KnowledgeCategory & { lessons_count: number })[];
    total: number;
  }> {
    try {
      const result = await apiCall('/knowledge-categories/with-counts');

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch categories with counts');
      }

      // Extract from result.data since sendSuccess wraps data
      return {
        categories: result.data.categories || [],
        total: result.data.total || 0
      };
    } catch (error) {
      console.error('Error fetching categories with counts:', error);
      throw error;
    }
  }

  /**
   * Get specific category by ID
   */
  static async getCategoryById(categoryId: string): Promise<KnowledgeCategory> {
    try {
      const result = await apiCall(`/knowledge-categories/${categoryId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch category');
      }

      // Extract from result.data since sendSuccess wraps data
      return result.data;
    } catch (error) {
      console.error('Error fetching category:', error);
      throw error;
    }
  }

  /**
   * Create new knowledge category
   */
  static async createCategory(data: CreateCategoryData): Promise<KnowledgeCategory> {
    try {
      const result = await apiCall('/knowledge-categories', {
        method: 'POST',
        body: JSON.stringify(data)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to create category');
      }

      // Extract from result.data since sendSuccess wraps data
      return result.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  /**
   * Update knowledge category
   */
  static async updateCategory(categoryId: string, data: UpdateCategoryData): Promise<KnowledgeCategory> {
    try {
      const result = await apiCall(`/knowledge-categories/${categoryId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update category');
      }

      // Extract from result.data since sendSuccess wraps data
      return result.data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  /**
   * Delete knowledge category
   */
  static async deleteCategory(categoryId: string, deleteLessons: boolean = false): Promise<void> {
    try {
      const result = await apiCall(
        `/knowledge-categories/${categoryId}?deleteLessons=${deleteLessons}`,
        {
          method: 'DELETE'
        }
      );

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  /**
   * Toggle favorite status for a category
   */
  static async toggleFavorite(categoryId: string): Promise<{ is_favorite: boolean }> {
    try {
      const result = await apiCall(`/knowledge-categories/${categoryId}/favorite`, {
        method: 'POST'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update favorite status');
      }

      return result.data;
    } catch (error) {
      console.error('Error toggling favorite status:', error);
      throw error;
    }
  }

  /**
   * Build system prompt for lesson generation
   */
  private static buildLessonPrompt(
    category: KnowledgeCategory,
    pastLessons?: LessonSummary[],
    customInstruction?: string
  ): string {
    let prompt = category.prompt_template;

    if (pastLessons && pastLessons.length > 0) {
      const historyContext = pastLessons
        .map(l => `- "${l.lesson_name}": ${l.lesson_objective}`)
        .join('\n');
      
      prompt += `\n\nPREVIOUS LESSONS COVERED:\n${historyContext}\n\n`;
      prompt += `Generate a NEW lesson that builds upon or complements these topics. `;
      prompt += `Avoid repeating the same content and topics from previous lessons.\n`;
    }

    if (customInstruction) {
      prompt += `\n\nSPECIFIC TOPIC REQUEST:\n${customInstruction}\n`;
    }

    prompt += `\n\nRESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "lesson_name": "Short lesson title",
  "lesson_objective": "Brief description of what this lesson teaches",
  "markdown": "Complete lesson content in markdown format"
}

CRITICAL JSON FORMATTING:
- The markdown content should be well-structured with headers, lists, tables, etc.
- ALL newlines in the markdown must be represented as \\n in the JSON
- ALL backslashes must be escaped as \\\\
- ALL quotes must be escaped as \\"
- Code blocks should be formatted like: \\n\\n\`\`\`language\\ncode here\\n\`\`\`\\n\\n
- Make absolutely sure the JSON is valid - test it mentally before outputting
- The content should be educational and engaging
- Do NOT wrap the JSON response in additional markdown code blocks`;

    return prompt;
  }

  /**
   * Generate a new lesson for a category
   */
  static async generateLesson(
    categoryId: string,
    selectedLessonIds: string[] = [],
    customInstruction?: string,
    onProgress?: (stage: 'text' | 'vocabulary') => void
  ): Promise<KnowledgeCategoryLesson> {
    try {
      console.log('🧠 [KnowledgeCategoryService] Generating new lesson...');

      // Fetch category details
      const category = await this.getCategoryById(categoryId);

      // Fetch selected past lessons for context
      let pastLessons: LessonSummary[] = [];
      if (selectedLessonIds.length > 0) {
        console.log('🧠 [KnowledgeCategoryService] Fetching past lessons for context...');
        const allLessons = await this.getPastLessons(category.category_slug, 100);
        pastLessons = allLessons.lessons.filter(l => selectedLessonIds.includes(l.lesson_id));
      }

      // Generate lesson content
      if (onProgress) onProgress('text');
      
      const systemPrompt = this.buildLessonPrompt(category, pastLessons, customInstruction);
      
      const messages = [
        {
          role: 'user' as const,
          content: customInstruction 
            ? `Generate a new lesson following the requirements. Focus on: ${customInstruction}`
            : 'Generate a new lesson following the requirements.'
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 8000,
        temperature: 0.8
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate lesson');
      }

      // Extract AI response
      let aiResponse: string = '';
      
      // Check result.data.content first (wrapped response)
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.data.response
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      // Check result.content array
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.response
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      // Check result.content as string
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🧠 [KnowledgeCategoryService] Failed to extract response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response');
      }

      console.log('🧠 [KnowledgeCategoryService] Raw AI response length:', aiResponse.length);

      // Parse JSON from the response
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present (only the outermost one)
      // Try multiple patterns to handle different AI formatting styles
      let codeBlockMatch = jsonString.match(/^```(?:json)?\s*\n([\s\S]*?)\n```$/);
      if (!codeBlockMatch) {
        // Try without newlines
        codeBlockMatch = jsonString.match(/^```(?:json)?([\s\S]*?)```$/);
      }
      if (codeBlockMatch) {
        jsonString = codeBlockMatch[1].trim();
      }
      
      // Check if JSON appears to be truncated
      if (!jsonString.endsWith('}') && !jsonString.endsWith(']')) {
        console.error('🧠 [KnowledgeCategoryService] JSON appears to be truncated');
        console.error('🧠 [KnowledgeCategoryService] Last 100 chars:', jsonString.slice(-100));
        throw new Error('AI response was incomplete. Please try again.');
      }
      
      let lessonData;
      try {
        lessonData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🧠 [KnowledgeCategoryService] JSON parse error:', parseError);
        throw new Error('Failed to parse AI response. Please try again.');
      }
      
      if (!lessonData || typeof lessonData !== 'object') {
        throw new Error('Invalid JSON response - expected an object');
      }
      
      if (!lessonData.lesson_name || !lessonData.lesson_objective || !lessonData.markdown) {
        throw new Error('Invalid lesson structure - missing required fields');
      }

      console.log('🧠 [KnowledgeCategoryService] Lesson generated successfully');

      // Save lesson to database
      const savedLesson = await this.saveLesson({
        lesson_name: lessonData.lesson_name,
        lesson_objective: lessonData.lesson_objective,
        category: category.category_slug,
        lesson_content: {
          markdown: lessonData.markdown
        }
      });

      return savedLesson;
    } catch (error) {
      console.error('🧠 [KnowledgeCategoryService] Error generating lesson:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to generate lessons.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Save lesson to database
   */
  static async saveLesson(lessonData: {
    lesson_name: string;
    lesson_objective: string;
    category: string;
    lesson_content: any;
  }): Promise<KnowledgeCategoryLesson> {
    try {
      const result = await apiCall('/lessons', {
        method: 'POST',
        body: JSON.stringify(lessonData)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to save lesson');
      }

      // Extract from result.data since sendSuccess wraps data
      const data = result.data;
      const savedLesson: KnowledgeCategoryLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        lesson_content: data.lesson_content,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return savedLesson;
    } catch (error) {
      console.error('🧠 [KnowledgeCategoryService] Error saving lesson:', error);
      throw error;
    }
  }

  /**
   * Get past lessons for category
   */
  static async getPastLessons(categorySlug: string, limit: number = 50, offset: number = 0): Promise<{
    lessons: LessonSummary[];
    total: number;
  }> {
    try {
      const result = await apiCall(`/lessons?category=${categorySlug}&limit=${limit}&offset=${offset}&sortBy=created_at&order=desc`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch lessons');
      }

      // Extract from result.data since sendSuccess wraps data
      return {
        lessons: result.data.lessons || [],
        total: result.data.total || 0
      };
    } catch (error) {
      console.error('🧠 [KnowledgeCategoryService] Error fetching past lessons:', error);
      throw error;
    }
  }

  /**
   * Get specific lesson by ID
   */
  static async getLessonById(lessonId: string): Promise<KnowledgeCategoryLesson> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch lesson');
      }

      // Extract from result.data since sendSuccess wraps data
      const data = result.data;
      const lesson: KnowledgeCategoryLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        lesson_content: data.lesson_content,
        image_url: data.image_url,
        image_prompt: data.image_prompt,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return lesson;
    } catch (error) {
      console.error('🧠 [KnowledgeCategoryService] Error fetching lesson:', error);
      throw error;
    }
  }

  /**
   * Delete lesson
   */
  static async deleteLesson(lessonId: string): Promise<void> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`, {
        method: 'DELETE'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete lesson');
      }
    } catch (error) {
      console.error('🧠 [KnowledgeCategoryService] Error deleting lesson:', error);
      throw error;
    }
  }

  /**
   * Generate image for a lesson using AI
   */
  static async generateLessonImage(
    lessonId: string,
    prompt: string,
    provider?: 'gemini' | 'grok'
  ): Promise<{ image: string; mimeType: string }> {
    try {
      return await generateImageForLesson(lessonId, prompt, provider);
    } catch (error) {
      console.error('🧠 [KnowledgeCategoryService] Error generating image:', error);
      throw error;
    }
  }

  /**
   * Upload image to Firebase Storage and save URL to lesson
   */
  static async uploadLessonImage(
    lessonId: string,
    imageData: string,
    mimeType: string,
    prompt: string
  ): Promise<string> {
    try {
      // Convert base64 to blob
      const base64Response = await fetch(`data:${mimeType};base64,${imageData}`);
      const blob = await base64Response.blob();

      // Upload to Firebase Storage
      const { storage } = await import('../firebase/config');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');

      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(7);
      const extension = mimeType.split('/')[1] || 'png';
      const fileName = `${timestamp}_${randomId}.${extension}`;
      const storagePath = `lessons/${lessonId}/images/${fileName}`;

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, blob, { contentType: mimeType });

      // Get public URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update lesson with image URL and prompt
      await apiCall(`/lessons/${lessonId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          image_url: downloadURL,
          image_prompt: prompt
        })
      });

      return downloadURL;
    } catch (error) {
      console.error('🧠 [KnowledgeCategoryService] Error uploading image:', error);
      throw error;
    }
  }

  /**
   * Generate image for a category using AI
   */
  static async generateCategoryImage(
    categoryId: string,
    prompt: string
  ): Promise<{ image: string; mimeType: string }> {
    try {
      return await generateImageForCategory(categoryId, prompt);
    } catch (error) {
      console.error('🧠 [KnowledgeCategoryService] Error generating image:', error);
      throw error;
    }
  }

  /**
   * Upload image to Firebase Storage and save URL to category
   */
  static async uploadCategoryImage(
    categoryId: string,
    imageData: string,
    mimeType: string,
    prompt: string
  ): Promise<string> {
    try {
      // Convert base64 to blob
      const base64Response = await fetch(`data:${mimeType};base64,${imageData}`);
      const blob = await base64Response.blob();

      // Upload to Firebase Storage
      const { storage } = await import('../firebase/config');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');

      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(7);
      const extension = mimeType.split('/')[1] || 'png';
      const fileName = `${timestamp}_${randomId}.${extension}`;
      const storagePath = `categories/${categoryId}/images/${fileName}`;

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, blob, { contentType: mimeType });

      // Get public URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update category with image URL and prompt
      await apiCall(`/knowledge-categories/${categoryId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          image_url: downloadURL,
          image_prompt: prompt
        })
      });

      return downloadURL;
    } catch (error) {
      console.error('🧠 [KnowledgeCategoryService] Error uploading image:', error);
      throw error;
    }
  }

  /**
   * Create a LessonService instance for a specific category
   * This allows the generic components to work with this service
   */
  static createLessonService(categorySlug: string): LessonService<KnowledgeCategoryLesson> {
    return {
      getPastLessons: (limit: number, offset?: number) => 
        this.getPastLessons(categorySlug, limit, offset),
      getLessonById: (lessonId: string) => 
        this.getLessonById(lessonId),
      deleteLesson: (lessonId: string) => 
        this.deleteLesson(lessonId)
    };
  }
}

